// Helpers for tour scheduling (one-off vs recurring weekday schedules).

export const WEEKDAYS = [
  { v: 1, short: 'Mon' }, { v: 2, short: 'Tue' }, { v: 3, short: 'Wed' },
  { v: 4, short: 'Thu' }, { v: 5, short: 'Fri' }, { v: 6, short: 'Sat' }, { v: 0, short: 'Sun' },
];
const SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  const d = new Date(ts);
  return isNaN(d) ? null : d;
}

export function isRecurring(tour) {
  return tour?.scheduleType === 'recurring' && Array.isArray(tour?.runsOn) && tour.runsOn.length > 0;
}

export function toYMDLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// The organizer cancelled this one occurrence (see cancelTourDate) — the rest
// of the recurring schedule is unaffected.
function isCancelledDate(tour, ymd) {
  return Array.isArray(tour?.cancelledDates) && tour.cancelledDates.includes(ymd);
}

// "Mon, Wed, Fri" — ordered Mon-first; "Every day" when all 7.
export function formatRunsOn(runsOn) {
  if (!runsOn?.length) return '';
  if (runsOn.length === 7) return 'Every day';
  return WEEKDAYS.filter(d => runsOn.includes(d.v)).map(d => d.short).join(', ');
}

// Next date (local) the recurring tour runs on, ≥ today and within the optional
// season. Returns a Date or null. One-off tours just return their startDate.
export function nextOccurrence(tour, from = new Date()) {
  if (!isRecurring(tour)) return toDate(tour?.startDate);
  const seasonStart = toDate(tour.seasonStart);
  const seasonEnd   = toDate(tour.seasonEnd);
  let cur = new Date(from); cur.setHours(0, 0, 0, 0);
  if (seasonStart && seasonStart > cur) cur = new Date(seasonStart);
  for (let i = 0; i < 366; i++) {
    if (tour.runsOn.includes(cur.getDay()) && (!seasonEnd || cur <= seasonEnd) && !isCancelledDate(tour, toYMDLocal(cur))) {
      return new Date(cur);
    }
    if (seasonEnd && cur > seasonEnd) return null;
    cur.setDate(cur.getDate() + 1);
  }
  return null;
}

// Validate a YYYY-MM-DD string against a recurring tour's schedule.
export function isValidTourDate(dateStr, tour) {
  if (!dateStr) return false;
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (date < today) return false;
  if (isRecurring(tour) && !tour.runsOn.includes(date.getDay())) return false;
  if (isCancelledDate(tour, dateStr)) return false;
  const ss = toDate(tour?.seasonStart), se = toDate(tour?.seasonEnd);
  if (ss && date < new Date(ss.getFullYear(), ss.getMonth(), ss.getDate())) return false;
  if (se && date > se) return false;
  return true;
}

// Is today a day this tour actually runs? One-off: within [startDate, endDate];
// recurring: today is a valid run day (weekday in runsOn, within season).
// Used to gate rider check-in to the day of the event.
export function isCheckInDay(tour, now = new Date()) {
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  if (isRecurring(tour)) {
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return isValidTourDate(`${y}-${m}-${d}`, tour);
  }
  const sd = toDate(tour?.startDate);
  if (!sd) return false;
  const start = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate());
  const ed = toDate(tour?.endDate);
  const end = ed ? new Date(ed.getFullYear(), ed.getMonth(), ed.getDate()) : start;
  return today >= start && today <= end;
}

// Has the tour already happened? One-off: its end (or start) date is before
// today. Recurring: there are no more upcoming run days (season has ended).
export function isTourPast(tour, now = new Date()) {
  const today = new Date(now); today.setHours(0, 0, 0, 0);
  if (isRecurring(tour)) return nextOccurrence(tour, today) === null;
  const end = tour?.endDate || tour?.startDate;
  const d = toDate(end);
  if (!d) return false;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()) < today;
}

export function fmtDateShort(dateOrTs) {
  const d = toDate(dateOrTs);
  return d ? d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

// ── Lesson time-slot overlap (private lessons book by the hour, not the day) ──
export function timeToMinutes(t) {
  const [h, m] = String(t || '0:0').split(':').map(Number);
  return h * 60 + m;
}

// Do two [start, start+hours) ranges (same day) overlap? Touching edges
// (one ends exactly when the other starts) don't count as an overlap.
export function intervalsOverlap(aStart, aHours, bStart, bHours) {
  const aS = timeToMinutes(aStart), aE = aS + Math.round((aHours || 0) * 60);
  const bS = timeToMinutes(bStart), bE = bS + Math.round((bHours || 0) * 60);
  return aS < bE && bS < aE;
}

export function minutesToHM(mins) {
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

// Every bookable start time for a lesson of `hours` duration inside
// [winStart, winEnd), stepped every `stepMinutes`, with any slot that would
// overlap an existing booking (same shape as a registration doc: { startTime,
// hours }) flagged `disabled` — including the rider's OWN existing booking on
// that date, since the coach isn't free then either way. Pulled out of
// TourDetailModal so this — the actual conflict-detection rider see — is
// covered by a test, not just eyeballed in the UI.
export function buildLessonSlotOptions({ winStart, winEnd, hours, bookings = [], stepMinutes = 30 }) {
  const winStartMin = timeToMinutes(winStart);
  const winEndMin = timeToMinutes(winEnd);
  const durMin = Math.round((hours || 0) * 60);
  const opts = [];
  for (let s = winStartMin; s + durMin <= winEndMin; s += stepMinutes) {
    const startStr = minutesToHM(s);
    const overlaps = bookings.some(b => intervalsOverlap(startStr, hours, b.startTime, b.hours));
    opts.push({ value: startStr, label: `${startStr}–${minutesToHM(s + durMin)}`, disabled: overlaps });
  }
  return opts;
}

// A short "how booked is this" label for an organizer's own tour/lesson card —
// shared by MyRidesPanel's "Organizing" tab and the business Dashboard so the
// two don't drift. A recurring tour's registrationCount is a LIFETIME total
// across every occurrence, not "how full is it right now" — for a capped one,
// show the next ride's own spots instead (matches the tour detail page, which
// shows per-date capacity for the same reason).
export function describeBookings(tour) {
  const regs = tour.registrationCount || 0;
  const recurring = isRecurring(tour);
  const isLesson = tour.category === 'lesson';
  if (isLesson && recurring) {
    // A lesson has no headcount cap — bookings fill the day hour-by-hour —
    // so show how many slots the next available day already has, not a
    // capacity fraction (which no longer applies even on older lesson docs
    // that still carry a stale maxParticipants:1 from before overlap
    // checking existed).
    const nd = nextOccurrence(tour);
    const used = nd ? (tour.dateCounts?.[toYMDLocal(nd)] || 0) : 0;
    return used > 0 ? `${used} booked next ride` : 'next ride open';
  }
  if (recurring && tour.maxParticipants) {
    const nd = nextOccurrence(tour);
    const used = nd ? (tour.dateCounts?.[toYMDLocal(nd)] || 0) : 0;
    return `${used}/${tour.maxParticipants} next ride`;
  }
  return `${regs}${!isLesson && tour.maxParticipants ? `/${tour.maxParticipants}` : ''} ${isLesson ? 'bookings' : 'registered'}`;
}

export { SHORT as WEEKDAY_SHORT };
